import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
  Phone, CheckCircle, XCircle, Clock, AlertTriangle, 
  Users, Activity, Eye, Volume2, ChevronRight, Shield,
  Radio, Megaphone, UserCheck
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Progress } from "./components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scanline overlay
const ScanlineOverlay = () => <div className="scanline-overlay" />;

// Countdown Timer Component
const CountdownTimer = ({ duration, startTime, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
      const remaining = Math.max(0, duration - elapsed);
      setTimeLeft(remaining);
      setProgress((remaining / duration) * 100);

      if (remaining === 0) {
        onExpire?.();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [duration, startTime, onExpire]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-muted-foreground uppercase">Temps restant</span>
        <span className={`font-mono text-2xl font-bold ${timeLeft < 30 ? 'text-destructive animate-pulse' : 'text-primary'}`}>
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </span>
      </div>
      <Progress 
        value={progress} 
        className={`h-2 ${timeLeft < 30 ? '[&>div]:bg-destructive' : '[&>div]:bg-primary'}`}
      />
    </div>
  );
};

// Active Task Panel (Big red border)
const ActiveTaskPanel = ({ task, onComplete, onFail }) => {
  const [confirming, setConfirming] = useState(null);

  const taskTypeIcons = {
    phone_call: <Phone className="w-8 h-8" />,
    physical_intervention: <Users className="w-8 h-8" />,
    announcement: <Megaphone className="w-8 h-8" />,
  };

  const handleComplete = () => {
    if (confirming === 'complete') {
      onComplete();
      setConfirming(null);
    } else {
      setConfirming('complete');
    }
  };

  const handleFail = () => {
    if (confirming === 'fail') {
      onFail();
      setConfirming(null);
    } else {
      setConfirming('fail');
    }
  };

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="border-4 border-destructive bg-destructive/10 p-6 relative overflow-hidden"
      data-testid="active-task-panel"
    >
      {/* Pulsing border effect */}
      <div className="absolute inset-0 border-4 border-destructive animate-pulse opacity-50 pointer-events-none" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-destructive/20 text-destructive">
            {taskTypeIcons[task.task_type] || <Activity className="w-8 h-8" />}
          </div>
          <div>
            <Badge className="badge-matrix alert mb-1">ACTION REQUISE</Badge>
            <h2 className="text-2xl font-bold heading text-destructive">{task.title}</h2>
          </div>
        </div>
        <div className="text-right">
          <span className="font-mono text-xs text-muted-foreground block">Tentative</span>
          <span className="font-mono text-xl font-bold text-destructive">
            {task.attempts}/{task.max_attempts}
          </span>
        </div>
      </div>

      {/* Target Person */}
      {task.target_person && (
        <div className="bg-card/50 p-3 mb-4 border border-border">
          <span className="font-mono text-xs text-muted-foreground block mb-1">CONTACTER</span>
          <span className="text-xl font-bold text-primary">{task.target_person}</span>
        </div>
      )}

      {/* Description */}
      <div className="mb-4">
        <span className="font-mono text-xs text-muted-foreground block mb-2">MISSION</span>
        <p className="text-lg">{task.description}</p>
      </div>

      {/* Script (if any) - Large text for reading */}
      {task.script && (
        <div className="bg-card p-4 border-2 border-primary mb-4">
          <span className="font-mono text-xs text-primary block mb-2 uppercase">
            <Volume2 className="w-4 h-4 inline mr-2" />
            TEXTE À DIRE / LIRE
          </span>
          <p className="text-xl leading-relaxed whitespace-pre-wrap">{task.script}</p>
        </div>
      )}

      {/* Countdown Timer */}
      <div className="mb-6">
        <CountdownTimer 
          duration={task.duration_seconds} 
          startTime={task.activated_at}
          onExpire={() => toast.warning("Temps écoulé !")}
        />
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4">
        <Button
          size="lg"
          className={`h-20 text-xl font-bold uppercase tracking-wider transition-all ${
            confirming === 'complete' 
              ? 'bg-primary text-primary-foreground animate-pulse' 
              : 'bg-primary/20 text-primary hover:bg-primary hover:text-primary-foreground'
          }`}
          onClick={handleComplete}
          data-testid="task-complete-btn"
        >
          <CheckCircle className="w-8 h-8 mr-3" />
          {confirming === 'complete' ? 'CONFIRMER' : 'OK - FAIT'}
        </Button>
        
        <Button
          size="lg"
          className={`h-20 text-xl font-bold uppercase tracking-wider transition-all ${
            confirming === 'fail' 
              ? 'bg-destructive text-destructive-foreground animate-pulse' 
              : 'bg-destructive/20 text-destructive hover:bg-destructive hover:text-destructive-foreground'
          }`}
          onClick={handleFail}
          data-testid="task-fail-btn"
        >
          <XCircle className="w-8 h-8 mr-3" />
          {confirming === 'fail' ? 'CONFIRMER' : 'ÉCHEC'}
        </Button>
      </div>

      {confirming && (
        <p className="text-center text-sm text-muted-foreground mt-3 font-mono">
          Cliquez à nouveau pour confirmer
        </p>
      )}
    </motion.div>
  );
};

// Pending Task Card
const PendingTaskCard = ({ task, scenarioTime }) => {
  const isUpcoming = task.scheduled_time_offset <= scenarioTime + 10;

  return (
    <div 
      className={`p-4 border transition-colors ${
        isUpcoming 
          ? 'border-yellow-500 bg-yellow-500/10' 
          : 'border-border bg-card/50'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <Badge className={`badge-matrix ${isUpcoming ? 'warning' : ''}`}>
          T+{task.scheduled_time_offset}min
        </Badge>
        <span className="font-mono text-xs text-muted-foreground">
          {task.attempts > 0 ? `Tentative ${task.attempts}/${task.max_attempts}` : 'En attente'}
        </span>
      </div>
      <h4 className="font-semibold">{task.title}</h4>
      <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
    </div>
  );
};

// Completed Task Card
const CompletedTaskCard = ({ task }) => {
  const isSuccess = task.status === 'completed';

  return (
    <div className={`p-3 border opacity-60 ${isSuccess ? 'border-primary/30' : 'border-destructive/30'}`}>
      <div className="flex items-center gap-2">
        {isSuccess ? (
          <CheckCircle className="w-4 h-4 text-primary" />
        ) : (
          <XCircle className="w-4 h-4 text-destructive" />
        )}
        <span className={`text-sm ${isSuccess ? 'text-primary' : 'text-destructive'}`}>
          {task.title}
        </span>
        <Badge className={`badge-matrix text-[10px] ${isSuccess ? '' : 'alert'}`}>
          {isSuccess ? 'OK' : task.status === 'abandoned' ? 'ABANDONNÉ' : 'ÉCHEC'}
        </Badge>
      </div>
    </div>
  );
};

// Main Animator Dashboard Component
const AnimatorSecondaryDashboard = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const accessToken = searchParams.get('token');
  
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [animator, setAnimator] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [scenarioTime, setScenarioTime] = useState("00:00");
  const [scenarioMinutes, setScenarioMinutes] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      if (accessToken) {
        const joinRes = await axios.get(`${API}/join/animator/${accessToken}`);
        setSession(joinRes.data.session);
        setScenario(joinRes.data.scenario);
        setAnimator(joinRes.data.animator);
        setTasks(joinRes.data.animator?.tasks || []);
        
        // Find active task
        const active = joinRes.data.animator?.tasks?.find(t => t.status === 'active');
        setActiveTask(active || null);
        
        // Get scenario time
        const timeRes = await axios.get(`${API}/sessions/${joinRes.data.session.id}/time`);
        setScenarioTime(timeRes.data.scenario_time);
        
        // Calculate scenario minutes from start
        if (joinRes.data.session.real_start_time) {
          const start = new Date(joinRes.data.session.real_start_time);
          const elapsed = (Date.now() - start.getTime()) / 1000 / 60;
          setScenarioMinutes(Math.floor(elapsed * (joinRes.data.session.time_compression || 1)));
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  }, [accessToken]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleTaskComplete = async () => {
    if (!activeTask) return;
    try {
      await axios.post(`${API}/sessions/${session.id}/tasks/${activeTask.id}/complete`, {
        status: "completed"
      });
      toast.success("Tâche accomplie !");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleTaskFail = async () => {
    if (!activeTask) return;
    try {
      const res = await axios.post(`${API}/sessions/${session.id}/tasks/${activeTask.id}/complete`, {
        status: "failed",
        failure_reason: "Échec signalé par l'animateur"
      });
      
      if (res.data.status === 'abandoned') {
        toast.error("Tâche abandonnée après 3 échecs");
      } else {
        toast.warning(`Échec - Nouvelle tentative prévue (${res.data.attempts}/${res.data.max_attempts})`);
      }
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => ['completed', 'failed', 'abandoned'].includes(t.status));

  if (loading) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center">
        <div className="loading-dot" />
        <div className="loading-dot" />
        <div className="loading-dot" />
      </div>
    );
  }

  if (!animator) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center">
        <div className="crisis-panel p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="heading text-xl text-destructive">Accès Invalide</h2>
          <p className="text-muted-foreground mt-2">Token animateur non reconnu</p>
          <Button className="mt-4" onClick={() => navigate("/")}>
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen matrix-bg">
      <ScanlineOverlay />
      
      {/* Header */}
      <div className="crisis-header">
        <div className="flex items-center gap-4">
          <UserCheck className="w-8 h-8 text-primary" />
          <div>
            <h1 className="heading text-lg text-primary">{animator.name}</h1>
            <p className="font-mono text-xs text-muted-foreground">
              Rôles: {animator.assigned_roles?.join(", ") || "Aucun rôle assigné"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <span className="font-mono text-xs text-muted-foreground block">HEURE SCÉNARIO</span>
            <span className="digital-clock text-2xl">{scenarioTime}</span>
          </div>
          <div className={`status-dot ${session?.status === 'running' ? 'active' : 'paused'}`} />
        </div>
      </div>

      {/* Pause Overlay */}
      <AnimatePresence>
        {session?.status === 'paused' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pause-overlay"
          >
            <div className="text-center">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="pause-text"
              >
                PAUSE
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="p-4 max-w-4xl mx-auto space-y-4">
        {/* Active Task - Full Width, Big */}
        {activeTask ? (
          <ActiveTaskPanel 
            task={activeTask}
            onComplete={handleTaskComplete}
            onFail={handleTaskFail}
          />
        ) : (
          <div className="crisis-panel p-8 text-center">
            <Eye className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="heading text-xl text-muted-foreground">EN ATTENTE</h2>
            <p className="text-muted-foreground mt-2">
              {pendingTasks.length > 0 
                ? `${pendingTasks.length} tâche(s) à venir` 
                : "Aucune tâche assignée pour le moment"}
            </p>
          </div>
        )}

        {/* Upcoming Tasks */}
        {pendingTasks.length > 0 && (
          <div className="crisis-panel p-4">
            <h3 className="heading text-lg text-primary mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Tâches à venir ({pendingTasks.length})
            </h3>
            <div className="space-y-2">
              {pendingTasks
                .sort((a, b) => a.scheduled_time_offset - b.scheduled_time_offset)
                .map(task => (
                  <PendingTaskCard 
                    key={task.id} 
                    task={task} 
                    scenarioTime={scenarioMinutes}
                  />
                ))
              }
            </div>
          </div>
        )}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div className="crisis-panel p-4">
            <h3 className="heading text-lg text-muted-foreground mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Tâches terminées ({completedTasks.length})
            </h3>
            <div className="space-y-1">
              {completedTasks.map(task => (
                <CompletedTaskCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        )}

        {/* Scenario Timeline (scrollable) */}
        <div className="crisis-panel p-4">
          <h3 className="heading text-lg text-muted-foreground mb-4 flex items-center gap-2">
            <Radio className="w-5 h-5" /> Fil du scénario
          </h3>
          <ScrollArea className="h-[200px]">
            <div className="space-y-2">
              {scenario?.events?.map((event, i) => {
                const isPast = event.time_offset_minutes < scenarioMinutes;
                const isCurrent = Math.abs(event.time_offset_minutes - scenarioMinutes) < 5;
                
                return (
                  <div 
                    key={i} 
                    className={`p-2 border-l-2 pl-3 ${
                      isCurrent 
                        ? 'border-primary bg-primary/10' 
                        : isPast 
                          ? 'border-muted-foreground/30 opacity-50' 
                          : 'border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Badge className="badge-matrix text-[10px]">T+{event.time_offset_minutes}min</Badge>
                      <span className="text-sm font-medium">{event.title}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default AnimatorSecondaryDashboard;
